import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useBlockRefreshIf } from '../../../../hooks/useBlockRefreshIf';
import LangEnum from '../../../../lang/LangEnum';
import { captureErrorInApm } from '../../../../lib/apm/utils/captureErrorInApm.util';
import snackbarStore from '../../../../stores/Snackbar.store';
import { sleep } from '../../../../utils/generics';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../translation/TranslationContext';
import { useHandleHydrateOfflineDatabase } from '../../database/hooks/useHandleHydrateOfflineDatabase';
import { offlineDatabase } from '../../database/offlineDatabase/offlineDatabase';
import { offlineQueryClient } from '../../queryClient/offlineQueryClient';
import { offlineMetaRepository } from '../../repositories/offlineMeta.repository';
import { waitOfflineEncryptionKey } from '../../utils/waitOfflineEncryptionKey.util';
import { useOfflineDataDownloadInformation } from '../useOfflineDataDownloadInformation';
import { useHandleHydrateStores } from './hooks/useHandleHydrateStores';
import { useHandleWithIncompletelyCache } from './hooks/useHandleWithIncompletelyCache';
import { useQueryKeysAndFnToCache } from './hooks/useQueryKeysAndFnToCache/useQueryKeysAndFnToCache';
import { QueryKeysAndFnOptions } from './hooks/useQueryKeysAndFnToCache/useQueryKeysAndFnToCache.types';
import { OfflineImageCacheManager } from './hooks/useQueryKeysAndFnToCache/utils/offlineImageCacheManager';
import { useOfflineDownloadStatusStore } from './stores/offlineDownloadStatus/offlineDownloadStatus.store';
import { cacheStaticImages } from './utils/cacheStaticImages.util';
import OfflineDataDownloaderUtils from './utils/offlineDataDownloader.utils';
import OfflineDataDownloaderChunkValidator from './validators/offlineDataDownloaderChunk.validator';

export const useOfflineDataDownloader = () => {
  const { translate } = useTranslation();
  const { team } = useApplicationContext();
  const { handleMessage } = snackbarStore;

  const { getQueryKeysAndFn } = useQueryKeysAndFnToCache();

  const {
    removeCachedData,
    currentUserOfflineData,
    addAllKeys,
    initDownload,
    updateCachedKeys,
    finishedDownload,
    initUpdatingData,
    finishedUpdatingData,
    handleAddKeyToUpdateFlow,
  } = useOfflineDownloadStatusStore(
    useShallow((state) => ({
      currentUserOfflineData: state.cachedData,
      addAllKeys: state.addAllKeys,
      initDownload: state.initDownload,
      updateCachedKeys: state.updateCachedKeys,
      removeCachedData: state.removeCachedData,
      finishedDownload: state.finishedDownload,
      initUpdatingData: state.initUpdatingData,
      finishedUpdatingData: state.finishedUpdatingData,
      handleAddKeyToUpdateFlow: state.handleAddKeyToUpdateFlow,
    })),
  );

  useHandleHydrateStores();
  useHandleHydrateOfflineDatabase();

  const { totalSyncedPercentage, hasSyncedData, isSyncing, lastSyncedAt } =
    useOfflineDataDownloadInformation();

  useBlockRefreshIf({
    condition: isSyncing,
  });

  const removeOfflineData = useCallback(async () => {
    if (!team?.id) {
      return;
    }

    const tablesToRemove = offlineDatabase.tables.filter(
      (table) => ![offlineDatabase.orders.name, offlineDatabase.meta.name].includes(table.name),
    );

    try {
      await offlineDatabase.transaction('rw', tablesToRemove, async () => {
        const result = await Promise.allSettled(
          tablesToRemove.map(async (table) => {
            await table.clear();
          }),
        );

        const errors = result.filter((res) => res.status === 'rejected');

        if (errors.length) {
          const errorMessages = errors.map((error) => error.reason?.message);
          throw new Error(JSON.stringify(errorMessages));
        }
      });
    } catch (error: any) {
      captureErrorInApm.offlineError(error);

      console.error('removeOfflineData', error);

      throw error;
    }

    offlineQueryClient.clear();

    const offlineImageCacheManager = await OfflineImageCacheManager.create();

    await offlineImageCacheManager?.removeAll();

    removeCachedData({ teamId: team.id });
  }, [currentUserOfflineData?.allKeys, removeCachedData, team?.id]);

  useHandleWithIncompletelyCache({
    removeOfflineData,
  });

  const validateIfCanFinish = async (queryKeysAndFn: QueryKeysAndFnOptions) => {
    await new Promise<void>((resolve) => {
      const queriesWithCanFinishFunction = queryKeysAndFn.filter(
        (queryKeyAndFn) => queryKeyAndFn.validateIfCanFinishAllProcess,
      );

      if (!queriesWithCanFinishFunction.length) {
        resolve();
      }

      const intervalId = setInterval(() => {
        const canFinish = queriesWithCanFinishFunction.every((queryKeyAndFn) =>
          !queryKeyAndFn.validateIfCanFinishAllProcess
            ? true
            : queryKeyAndFn.validateIfCanFinishAllProcess(),
        );

        if (!canFinish) {
          return;
        }

        clearInterval(intervalId);
        resolve();
      }, 1000);
    });
  };

  const downloadAndCacheAllQueries = async (queryKeysAndFnToCache: QueryKeysAndFnOptions) => {
    await waitOfflineEncryptionKey();

    const chunkedQueryKeysAndFnToCache =
      OfflineDataDownloaderUtils.chunkByPriority(queryKeysAndFnToCache);

    const validator = new OfflineDataDownloaderChunkValidator(validateIfCanFinish);

    for (const chunk of chunkedQueryKeysAndFnToCache) {
      await validator.validateNext(chunk, queryKeysAndFnToCache);

      const chunkResults = await Promise.allSettled(
        chunk.map(async (queryKeyAndFn) => {
          if (!queryKeyAndFn.useReactQuery) {
            return queryKeyAndFn.queryFn();
          }

          offlineQueryClient.removeQueries({
            queryKey: queryKeyAndFn.queryKey,
          });
          return offlineQueryClient.fetchQuery({
            queryKey: queryKeyAndFn.queryKey,
            queryFn: queryKeyAndFn.queryFn,
            staleTime: Infinity,
          });
        }),
      );

      const hasError = chunkResults.some((result) => result.status === 'rejected');
      if (hasError) {
        captureErrorInApm.offlineError(
          new Error(
            `Error while caching queries: ${chunkResults
              .filter((result) => result.status === 'rejected')
              .map((result) => result.reason?.message)
              .join(', ')}`,
          ),
        );

        console.error('downloadAndCacheAllQueries', chunkResults);

        await validateIfCanFinish(queryKeysAndFnToCache);

        return false;
      }

      updateCachedKeys({
        keys: chunk.map((queryKeyAndFn) => queryKeyAndFn.queryKey),
      });
    }

    await cacheStaticImages();
    await validateIfCanFinish(queryKeysAndFnToCache);
    return true;
  };

  const onDownloadWithSuccess = useCallback(async () => {
    await offlineMetaRepository.updateCanEnterInOfflineMode(true);
    finishedDownload();
  }, [finishedDownload]);

  const onErrorDownloading = useCallback(async () => {
    await removeOfflineData();
  }, [removeOfflineData]);

  const downloadData = useCallback(async () => {
    if (!team?.id) {
      return;
    }

    initDownload({
      teamId: team.id,
    });

    try {
      const queryKeysAndFnToCache = await getQueryKeysAndFn();

      if (!queryKeysAndFnToCache.length) {
        throw new Error('no queries to cache');
      }

      const allKeys = queryKeysAndFnToCache.map((queryKeyAndFn) => queryKeyAndFn.queryKey);

      addAllKeys({
        keys: allKeys,
      });

      const cachedWithSuccess = await downloadAndCacheAllQueries(queryKeysAndFnToCache);

      if (!cachedWithSuccess) {
        throw new Error('error while caching queries');
      }

      await onDownloadWithSuccess();

      handleMessage({
        type: 'success',
        message: translate(LangEnum.OFFLINE_DOWNLOAD_DATA_SUCCESS),
      });
    } catch (error: any) {
      captureErrorInApm.offlineError(error);

      console.error('downloadData', error);

      handleMessage({
        type: 'error',
        message: translate(LangEnum.OFFLINE_DOWNLOAD_DATA_ERROR),
      });

      await onErrorDownloading();
    }
  }, [
    team?.id,
    addAllKeys,
    getQueryKeysAndFn,
    initDownload,
    removeOfflineData,
    onDownloadWithSuccess,
    onErrorDownloading,
  ]);

  const updateOfflineData = useCallback(async () => {
    initUpdatingData();
    await sleep(1);

    try {
      await removeOfflineData();

      await downloadData();
    } catch (error: any) {
      captureErrorInApm.offlineError(error);

      console.error('updateOfflineData', error);

      handleMessage({
        type: 'error',
        message: translate(LangEnum.OFFLINE_DOWNLOAD_DATA_ERROR),
      });
    } finally {
      finishedUpdatingData();
    }
  }, [downloadData, removeOfflineData]);

  const updatePartialData = async (queriesToUpdate: QueryKeysAndFnOptions) => {
    if (!hasSyncedData || !queriesToUpdate.length) {
      captureErrorInApm.offlineError(new Error('cannot update partial data before initial sync'));
      console.warn('updatePartialData called before initial sync or with no queries to update', {
        hasSyncedData,
        queriesToUpdate,
      });

      return;
    }

    initUpdatingData();
    handleAddKeyToUpdateFlow({
      keys: queriesToUpdate.map((queryKeyAndFn) => queryKeyAndFn.queryKey),
    });
    await sleep(1000);

    try {
      const cachedWithSuccess = await downloadAndCacheAllQueries(queriesToUpdate);

      if (!cachedWithSuccess) {
        throw new Error('error while caching queries');
      }

      finishedDownload();
    } catch (error: any) {
      captureErrorInApm.offlineError(error);

      console.error('updatePartialData', error);

      handleMessage({
        type: 'error',
        message: translate(LangEnum.OFFLINE_ERROR_ON_AUTO_UPDATE),
      });

      await removeOfflineData();
    } finally {
      finishedUpdatingData();
    }
  };

  return {
    isSyncing,
    lastSyncedAt,
    hasSyncedData,
    totalSyncedPercentage,
    downloadData,
    updateOfflineData,
    removeOfflineData,
    updatePartialData,
  };
};
